/* =========================================================================
   Reading Journey — modal + Add All to Cart
   - Uses Carnival's <modal-dialog>-style pattern (ESC/backdrop/body-lock)
     but is a self-contained custom element so it does not conflict with
     Carnival's own <modal-dialog>.
   - Relies on window.routes.cart_add_url (set in layout/theme.liquid)
   - Applies the discount through /discount/CODE before rendering cart totals
   ========================================================================= */

(function () {
  'use strict';

  class ReadingJourneyModal extends HTMLElement {
    constructor() {
      super();
      this._onKeydown = this._onKeydown.bind(this);
      this._onBackdropClick = this._onBackdropClick.bind(this);
      this._onCtaClick = this._onCtaClick.bind(this);
      this._onCloseClick = this._onCloseClick.bind(this);
    }

    connectedCallback() {
      if (this._moved) return;

      // Remove any stale copy of this modal that was previously moved to <body>.
      // This happens when Shopify re-renders the section in the Theme Customizer
      // after a setting change — a fresh element is created but the old one
      // (already appended to <body>) is still in the DOM.
      if (this.id) {
        document.querySelectorAll('body > [data-rj-moved]').forEach(function (el) {
          if (el.id === this.id) el.remove();
        }, this);
      }

      // Move to <body> so z-index stacking is consistent and sits above
      // Carnival's header/drawer.
      document.body.appendChild(this);
      this.setAttribute('data-rj-moved', '');
      this._moved = true;

      var closeBtn = this.querySelector('[data-rj-close]');
      if (closeBtn) closeBtn.addEventListener('click', this._onCloseClick);

      var cta = this.querySelector('[data-rj-cta]');
      if (cta) cta.addEventListener('click', this._onCtaClick);

      this.addEventListener('click', this._onBackdropClick);
    }

    show(opener) {
      this.openedBy = opener || null;
      this.setAttribute('open', '');
      document.body.classList.add('overflow-hidden');
      document.addEventListener('keydown', this._onKeydown);

      // Focus the close button first for keyboard users.
      var closeBtn = this.querySelector('[data-rj-close]');
      if (closeBtn) closeBtn.focus();
    }

    hide() {
      this.removeAttribute('open');
      document.body.classList.remove('overflow-hidden');
      document.removeEventListener('keydown', this._onKeydown);
      this._clearError();
      if (this.openedBy && typeof this.openedBy.focus === 'function') {
        this.openedBy.focus();
      }
    }

    _onKeydown(event) {
      if (event.key === 'Escape') {
        event.preventDefault();
        this.hide();
      }
    }

    _onBackdropClick(event) {
      // Only close when the backdrop itself (this element) is the target.
      if (event.target === this) this.hide();
    }

    _onCloseClick(event) {
      event.preventDefault();
      this.hide();
    }

    _showError(message) {
      var err = this.querySelector('[data-rj-error]');
      if (!err) return;
      err.textContent = message;
      err.setAttribute('data-visible', 'true');
    }

    _clearError() {
      var err = this.querySelector('[data-rj-error]');
      if (!err) return;
      err.textContent = '';
      err.setAttribute('data-visible', 'false');
    }

    async _renderDiscountedCartDrawer(discountCode, cartUrl) {
      var cartDrawer = document.querySelector('cart-drawer');
      if (!cartDrawer || typeof cartDrawer.renderContents !== 'function') return false;

      var sections = ['cart-drawer', 'cart-icon-bubble'];
      if (typeof cartDrawer.getSectionsToRender === 'function') {
        sections = cartDrawer.getSectionsToRender()
          .map(function (section) { return section.id; })
          .filter(Boolean);
      }

      var sectionsUrl = window.location.pathname || cartUrl;
      var sectionUrl =
        cartUrl +
        '?sections=' +
        encodeURIComponent(sections.join(',')) +
        '&sections_url=' +
        encodeURIComponent(sectionsUrl);

      var fetchUrl = sectionUrl;
      if (discountCode) {
        fetchUrl =
          '/discount/' +
          encodeURIComponent(discountCode) +
          '?redirect=' +
          encodeURIComponent(sectionUrl);
      }

      var response = await fetch(fetchUrl, {
        credentials: 'same-origin',
        cache: 'no-store',
        headers: {
          Accept: 'application/json',
        },
      });

      if (!response.ok) return false;

      var sectionsHtml = await response.json().catch(function () { return null; });
      if (!sectionsHtml || !sectionsHtml['cart-drawer']) return false;

      cartDrawer.renderContents({
        id: null,
        sections: sectionsHtml,
      });

      return true;
    }

    async _onCtaClick(event) {
      event.preventDefault();
      var cta = event.currentTarget;

      // Collect variant IDs declared by each game card.
      var variantIds = Array.from(
        this.querySelectorAll('[data-rj-variant-id]')
      )
        .map(function (el) { return el.getAttribute('data-rj-variant-id'); })
        .filter(Boolean);

      if (!variantIds.length) {
        this._showError('This journey has no products selected yet.');
        return;
      }

      var discountCode = (this.getAttribute('data-rj-discount') || '').trim();
      var cartUrl = (window.routes && window.routes.cart_url) || '/cart';
      var cartAddUrl = (window.routes && window.routes.cart_add_url) || '/cart/add.js';
      if (discountCode && typeof window.tfgSetActiveJourneyDiscount === 'function') {
        window.tfgSetActiveJourneyDiscount(discountCode);
      }

      cta.setAttribute('aria-busy', 'true');
      this._clearError();

      try {
        var response = await fetch(cartAddUrl, {
          method: 'POST',
          credentials: 'same-origin',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/javascript',
          },
          body: JSON.stringify({
            items: variantIds.map(function (id) {
              var item = { id: Number(id), quantity: 1 };
              if (discountCode) {
                item.properties = { _journey_discount_code: discountCode };
              }
              return item;
            }),
          }),
        });

        if (!response.ok) {
          var errBody = await response.json().catch(function () { return {}; });
          throw new Error(
            errBody.description ||
              errBody.message ||
              'Something went wrong adding these products to your cart.'
          );
        }

        if (discountCode) {
          window.location.href =
            typeof window.tfgBuildDiscountedCartUrl === 'function'
              ? window.tfgBuildDiscountedCartUrl(cartUrl, discountCode)
              : '/discount/' + encodeURIComponent(discountCode) + '?redirect=' + encodeURIComponent(cartUrl);
          return;
        }

        var renderedDrawer = await this._renderDiscountedCartDrawer(discountCode, cartUrl);
        if (renderedDrawer) {
          cta.removeAttribute('aria-busy');
          this.hide();
          return;
        }

        // Fallback for non-drawer contexts or if Shopify does not return sections.
        if (discountCode) {
          window.location.href =
            '/discount/' +
            encodeURIComponent(discountCode) +
            '?redirect=' +
            encodeURIComponent(cartUrl);
        } else {
          window.location.href = cartUrl;
        }
      } catch (err) {
        console.error('[reading-journey] add to cart failed', err);
        this._showError(
          (err && err.message) ||
            'Something went wrong. Please try again in a moment.'
        );
        cta.removeAttribute('aria-busy');
      }
    }
  }

  if (!customElements.get('reading-journey-modal')) {
    customElements.define('reading-journey-modal', ReadingJourneyModal);
  }

  /* ---------- opener wiring ----------
     Every banner has: <button class="reading-journey-banner" data-rj-open="#TargetId">
     We attach a click handler that looks up the matching <reading-journey-modal>
     and calls .show(). Idempotent across section reloads in the Theme Customizer.
  */
  function wireOpeners(root) {
    var scope = root && root.querySelectorAll ? root : document;
    var openers = scope.querySelectorAll('[data-rj-open]');
    openers.forEach(function (btn) {
      if (btn.__rjBound) return;
      btn.__rjBound = true;
      btn.addEventListener('click', function (ev) {
        ev.preventDefault();
        var selector = btn.getAttribute('data-rj-open');
        if (!selector) return;
        var modal = document.querySelector(selector);
        if (modal && typeof modal.show === 'function') modal.show(btn);
      });
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { wireOpeners(); });
  } else {
    wireOpeners();
  }

  // Re-wire after Theme Customizer section reloads.
  document.addEventListener('shopify:section:load', function (e) { wireOpeners(e.target); });

  document.addEventListener('shopify:block:select', function (e) {
    // In the Theme Customizer, auto-open the matching modal when the merchant
    // clicks on a Reading Journey block in the sidebar — BUT only if it is not
    // already open. Without this guard, every setting change triggers a
    // section re-render → block:select fires again → modal pops open even
    // after the merchant has dismissed it.
    var opener = e.target && e.target.querySelector('[data-rj-open]');
    if (!opener) return;
    var selector = opener.getAttribute('data-rj-open');
    var modal = document.querySelector(selector);
    if (modal && typeof modal.show === 'function' && !modal.hasAttribute('open')) {
      modal.show(opener);
    }
  });

  document.addEventListener('shopify:block:deselect', function (e) {
    var opener = e.target && e.target.querySelector('[data-rj-open]');
    if (!opener) return;
    var selector = opener.getAttribute('data-rj-open');
    var modal = document.querySelector(selector);
    if (modal && typeof modal.hide === 'function') modal.hide();
  });
})();
